import type { MathfieldElement } from 'mathlive';

let mathlivePromise: Promise<typeof import('mathlive')> | null = null;

/**
 * Import MathLive library with caching and font configuration
 */
export function importMathlive(): Promise<typeof import('mathlive')> {
  if (mathlivePromise) return mathlivePromise;

  mathlivePromise = import('mathlive').then((mathlive) => {
    if (typeof window === 'undefined') return mathlive;

    // Configure fonts directory globally
    mathlive.MathfieldElement.fontsDirectory = '/assets/mathlive/fonts';

    return mathlive;
  });

  return mathlivePromise;
}

/**
 * Configure math-field context menu by filtering disabled items
 */
export function configureMathfieldMenu(items: MathfieldElement['menuItems']): MathfieldElement['menuItems'] {
  return items.filter((item) => {
    return !['mode', 'background-color', 'font-size', 'font-family', 'color', 'variant'].includes(
      (item as Record<string, unknown>).id as string,
    );
  });
}

const MATH_EXPRESSION_FALLBACK_LABEL = 'Math expression';

/**
 * The accessible name for an expression: MathLive's spoken form, else the LaTeX itself.
 */
export function describeMathExpression(expression: string, mathlive: typeof import('mathlive')): string {
  return mathlive.convertLatexToSpeakableText?.(expression) || expression || MATH_EXPRESSION_FALLBACK_LABEL;
}

export interface MathfieldAccessibility {
  readonly label: string;
  readonly readOnly: boolean;
  readonly disabled: boolean;
  readonly invalid: boolean;
}

/**
 * Configure ARIA attributes for math-field accessibility
 */
export function configureMathfieldAccessibility(mathfield: MathfieldElement, accessibility: MathfieldAccessibility): void {
  const isInteractive = !accessibility.readOnly && !accessibility.disabled;
  mathfield.setAttribute('aria-label', accessibility.label);
  mathfield.setAttribute('role', isInteractive ? 'textbox' : 'img');
  setAriaState(mathfield, 'aria-disabled', accessibility.disabled);
  markMathfieldInvalid(mathfield, accessibility.invalid);
}

/** Announce whether the field's LaTeX is invalid; kept in step with typing, not only with loading. */
export function markMathfieldInvalid(mathfield: MathfieldElement, invalid: boolean): void {
  setAriaState(mathfield, 'aria-invalid', invalid);
}

function setAriaState(element: Element, name: 'aria-disabled' | 'aria-invalid', value: boolean): void {
  if (value) {
    element.setAttribute(name, 'true');
  } else {
    element.removeAttribute(name);
  }
}
